import { useEffect, useSyncExternalStore } from 'react';

import { request } from '@/lib/api';

export type FeatureSwitchRow = { key: string; on: boolean };

type Snapshot = { ready: boolean; on: ReadonlyMap<string, boolean> };

const empty = new Map<string, boolean>();
let snapshot: Snapshot = { ready: false, on: empty };
const listeners = new Set<() => void>();
let started = false;
let generation = 0;

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

function rowsFromPayload(payload: unknown): FeatureSwitchRow[] {
  if (!payload || typeof payload !== 'object') return [];
  const body = payload as { switches?: unknown; data?: { switches?: unknown } };
  const list = Array.isArray(body.switches)
    ? body.switches
    : Array.isArray(body.data?.switches)
      ? body.data.switches
      : [];
  return list.filter(
    (row): row is FeatureSwitchRow =>
      !!row &&
      typeof row === 'object' &&
      typeof (row as FeatureSwitchRow).key === 'string' &&
      typeof (row as FeatureSwitchRow).on === 'boolean',
  );
}

/** One GET /features for the session. A failure stays off. */
export function ensureFeatureSwitchesLoaded() {
  if (started) return;
  started = true;
  const gen = generation;
  void request
    .get<unknown>('/features')
    .then((payload) => {
      if (gen !== generation) return;
      snapshot = {
        ready: true,
        on: new Map(rowsFromPayload(payload).map((row) => [row.key, row.on])),
      };
      emit();
    })
    .catch(() => {
      if (gen !== generation) return;
      snapshot = { ready: true, on: empty };
      emit();
    });
}

/** False while the session list has not loaded, and false when the key is off or missing. */
export function useFeatureOn(key: string): boolean {
  useEffect(() => {
    ensureFeatureSwitchesLoaded();
  }, []);
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!snap.ready) return false;
  return snap.on.get(key) === true;
}

export function useFeatureSwitchState(key: string): 'pending' | 'on' | 'off' {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!snap.ready) return 'pending';
  return snap.on.get(key) === true ? 'on' : 'off';
}

export function __setFeatureSwitchesForTests(next: 'loading' | FeatureSwitchRow[]) {
  generation += 1;
  started = true;
  snapshot =
    next === 'loading'
      ? { ready: false, on: empty }
      : { ready: true, on: new Map(next.map((row) => [row.key, row.on])) };
  emit();
}

export function __resetFeatureSwitchesForTests() {
  generation += 1;
  started = false;
  snapshot = { ready: false, on: empty };
  emit();
}
